import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Shield, Users, Globe, ShoppingBag, DollarSign, FileText,
  Trash2, Pencil, CheckCircle2, XCircle, Loader2, ExternalLink,
  Sparkles, Send, Image, Percent, Settings, MessageSquare, BookOpen, Plus, Calendar, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { SafeHtml } from "@/components/SafeHtml";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import ArticleEditor from "@/components/ArticleEditor";
import AdvertiserPricingManager from "@/components/AdvertiserPricingManager";
import NegotiationsManager from "@/components/NegotiationsManager";
import BlogEditor from "@/components/BlogEditor";
import SeoSettingsManager from "@/components/SeoSettingsManager";
import LegalPagesManager from "@/components/LegalPagesManager";
import CommissionManager from "@/components/CommissionManager";
import ToolsShortcuts from "@/components/ToolsShortcuts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleBadge = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  publisher: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  advertiser: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
};

const statusBadge = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  accepted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  rejected: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
};

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [orderFilter, setOrderFilter] = useState("all");
  const [changingRole, setChangingRole] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorContinuing, setEditorContinuing] = useState(false);
  const [editorRegenerating, setEditorRegenerating] = useState(false);
  const [generatingArticle, setGeneratingArticle] = useState(false);
  const [allSites, setAllSites] = useState([]);
  const [selectedSiteForWidget, setSelectedSiteForWidget] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, ordersRes, wRes, sitesRes, blogRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/orders${orderFilter !== "all" ? `?status=${orderFilter}` : ""}`),
        axios.get(`${API}/admin/withdrawals`).catch(() => ({ data: [] })),
        axios.get(`${API}/sites`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blog`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
      setWithdrawals(wRes.data);
      setAllSites(sitesRes.data);
      setBlogPosts(blogRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t("admin.noAccess"));
      }
      console.error(err);
    }
    finally { setLoading(false); }
  }, [orderFilter, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { role: newRole });
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setChangingRole(null); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
  };

  const handleOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status });
      toast.success(t("common.success"));
      setSelectedOrder(null);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Ladowanie...</p></div>;

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("admin.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">{t("admin.subtitle")}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: t("admin.users"), value: stats.users.total, icon: Users, color: "text-blue-500", sub: `${stats.users.advertisers}A / ${stats.users.publishers}P / ${stats.users.admins}Ad` },
            { label: t("admin.sites"), value: stats.sites, icon: Globe, color: "text-emerald-500" },
            { label: t("admin.orders"), value: stats.orders.total, icon: ShoppingBag, color: "text-amber-500", sub: `${stats.orders.pending} pending` },
            { label: t("admin.revenue"), value: `${stats.revenue} PLN`, icon: DollarSign, color: "text-primary" },
            { label: t("admin.posts"), value: stats.posts, icon: FileText, color: "text-slate-500" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3">
                <s.icon className={`w-4 h-4 ${s.color} mb-1`} strokeWidth={1.5} />
                <p className="text-xl font-bold font-heading text-foreground">{s.value}</p>
                <p className="text-[9px] text-muted-foreground">{s.label}</p>
                {s.sub && <p className="text-[8px] text-muted-foreground/60 mt-0.5">{s.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ToolsShortcuts />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="admin-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="overview" className="gap-1.5"><Shield className="w-3.5 h-3.5" /> {t("admin.overviewTab")}</TabsTrigger>
          <TabsTrigger value="users" className="gap-1.5"><Users className="w-3.5 h-3.5" /> {t("admin.usersTab")} <Badge variant="secondary" className="text-[10px] ml-1">{users.length}</Badge></TabsTrigger>
          <TabsTrigger value="orders" className="gap-1.5"><ShoppingBag className="w-3.5 h-3.5" /> {t("admin.ordersTab")} <Badge variant="secondary" className="text-[10px] ml-1">{orders.length}</Badge></TabsTrigger>
          <TabsTrigger value="sites" className="gap-1.5"><Globe className="w-3.5 h-3.5" /> Strony</TabsTrigger>
          <TabsTrigger value="commission" className="gap-1.5"><Percent className="w-3.5 h-3.5" /> Prowizja</TabsTrigger>
          <TabsTrigger value="pricing" className="gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Ceny indyw.</TabsTrigger>
          <TabsTrigger value="negotiations" className="gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Negocjacje</TabsTrigger>
          <TabsTrigger value="blog" className="gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Blog</TabsTrigger>
          <TabsTrigger value="seo" className="gap-1.5"><Settings className="w-3.5 h-3.5" /> SEO</TabsTrigger>
          <TabsTrigger value="legal" className="gap-1.5"><FileText className="w-3.5 h-3.5" /> Strony prawne</TabsTrigger>
          <TabsTrigger value="withdrawals" className="gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Wyplaty</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> {t("admin.userBreakdown")}</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1 space-y-2">
                  {[
                    { label: t("auth.advertiser"), value: stats.users.advertisers, bg: roleBadge.advertiser },
                    { label: t("auth.publisher"), value: stats.users.publishers, bg: roleBadge.publisher },
                    { label: "Admin", value: stats.users.admins, bg: roleBadge.admin },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <Badge className={`text-[10px] border-0 rounded-md ${r.bg}`}>{r.label}</Badge>
                      <span className="text-sm font-bold text-foreground">{r.value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-amber-500" /> {t("admin.orderBreakdown")}</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1 space-y-2">
                  <div className="flex justify-between"><span className="text-xs text-muted-foreground">Pending</span><span className="text-sm font-bold text-amber-500">{stats.orders.pending}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-muted-foreground">Completed</span><span className="text-sm font-bold text-emerald-500">{stats.orders.completed}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-muted-foreground">Total</span><span className="text-sm font-bold">{stats.orders.total}</span></div>
                </CardContent>
              </Card>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><DollarSign className="w-4 h-4 text-primary" /> {t("admin.financials")}</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1">
                  <p className="text-2xl font-bold text-primary">{stats.revenue} PLN</p>
                  <p className="text-[10px] text-muted-foreground">{t("admin.totalRevenue")}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold">{t("admin.userIdent")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("admin.displayName")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("admin.role")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("admin.registered")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">{t("sites.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="text-xs font-medium">{u.identifier}</TableCell>
                      <TableCell className="text-xs">{u.display_name || "—"}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v) => handleRoleChange(u.id, v)} disabled={changingRole === u.id}>
                          <SelectTrigger className="h-7 w-[120px] text-[10px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="advertiser" className="text-xs">{t("auth.advertiser")}</SelectItem>
                            <SelectItem value="publisher" className="text-xs">{t("auth.publisher")}</SelectItem>
                            <SelectItem value="admin" className="text-xs">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteUser(u.id)}>
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-4 space-y-3">
          <Select value={orderFilter} onValueChange={setOrderFilter}>
            <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">{t("queue.all")}</SelectItem>
              <SelectItem value="pending" className="text-xs">Pending</SelectItem>
              <SelectItem value="accepted" className="text-xs">Accepted</SelectItem>
              <SelectItem value="completed" className="text-xs">Completed</SelectItem>
              <SelectItem value="rejected" className="text-xs">Rejected</SelectItem>
            </SelectContent>
          </Select>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Lista zamowien */}
            <div className={selectedOrder ? "" : "lg:col-span-3"}>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardContent className="p-0">
                  {orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <ShoppingBag className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                      <p className="text-xs">{t("admin.noOrders")}</p>
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[500px]">
                      <div className="divide-y">
                        {orders.map((o) => (
                          <button key={o.id} onClick={() => setSelectedOrder(o)}
                            className={`w-full text-left p-3 hover:bg-accent/30 transition-colors ${selectedOrder?.id === o.id ? "bg-primary/5" : ""}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-xs font-semibold text-foreground">{o.offer_name || o.order_type || "Zamowienie"}</p>
                                <p className="text-[10px] text-muted-foreground">{o.site_name || "—"} | {o.user_name || o.guest_email || "Gosc"}</p>
                                <p className="text-[9px] text-muted-foreground">{new Date(o.created_at).toLocaleString()}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-bold">{o.total_price || o.price} PLN</p>
                                <Badge className={`text-[8px] border-0 rounded-md ${statusBadge[o.status] || statusBadge.pending}`}>{o.status}</Badge>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Szczegoly zamowienia */}
            {selectedOrder && (
              <div className="lg:col-span-2">
                <Card className="rounded-xl border shadow-sm bg-card">
                  <CardHeader className="pb-2 pt-4 px-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base font-heading">{selectedOrder.offer_name || selectedOrder.order_type || "Zamowienie"}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-0.5">ID: {selectedOrder.id.substring(0, 12)}... | {new Date(selectedOrder.created_at).toLocaleString()}</p>
                      </div>
                      <Badge className={`text-xs border-0 rounded-lg px-3 py-1 ${statusBadge[selectedOrder.status] || statusBadge.pending}`}>{selectedOrder.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 pt-2 space-y-4">
                    {/* Info glowne */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Portal</p>
                        <p className="text-sm font-semibold text-foreground">{selectedOrder.site_name || "—"}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Klient</p>
                        <p className="text-sm font-semibold text-foreground">{selectedOrder.user_name || selectedOrder.guest_email || "Gosc"}</p>
                      </div>
                    </div>

                    {/* Cena */}
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Cena uslugi:</span>
                        <span className="text-lg font-bold text-blue-700">{selectedOrder.price} PLN</span>
                      </div>
                      {selectedOrder.ai_format_cost > 0 && (
                        <div className="flex justify-between text-xs text-blue-600 mt-1">
                          <span>Formatowanie AI:</span><span>+{selectedOrder.ai_format_cost} PLN</span>
                        </div>
                      )}
                      {selectedOrder.image_resize_cost > 0 && (
                        <div className="flex justify-between text-xs text-blue-600">
                          <span>Resize zdjecia:</span><span>+{selectedOrder.image_resize_cost} PLN</span>
                        </div>
                      )}
                      {(selectedOrder.total_price && selectedOrder.total_price !== selectedOrder.price) && (
                        <div className="flex justify-between text-sm font-bold text-blue-800 mt-1 pt-1 border-t border-blue-200">
                          <span>LACZNIE:</span><span>{selectedOrder.total_price} PLN</span>
                        </div>
                      )}
                    </div>

                    {/* Fraza i link */}
                    {selectedOrder.target_phrase && (
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 space-y-1">
                        <p className="text-[9px] text-emerald-700 uppercase tracking-wider font-semibold">Fraza i link do artykulu</p>
                        <p className="text-sm"><strong>Fraza:</strong> {selectedOrder.target_phrase}</p>
                        {selectedOrder.target_url && (
                          <p className="text-sm"><strong>URL:</strong> <a href={selectedOrder.target_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedOrder.target_url}</a></p>
                        )}
                      </div>
                    )}

                    {/* Brief (wydawca pisze) */}
                    {selectedOrder.article_brief && (
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 space-y-1">
                        <p className="text-[9px] text-amber-700 uppercase tracking-wider font-semibold">Brief artykulu (wydawca ma napisac)</p>
                        <p className="text-sm text-foreground">{selectedOrder.article_brief}</p>
                      </div>
                    )}

                    {/* Tytul artykulu */}
                    {selectedOrder.title && (
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Tytul artykulu</p>
                        <p className="text-base font-semibold text-foreground">{selectedOrder.title}</p>
                      </div>
                    )}

                    {/* Meta */}
                    {(selectedOrder.meta_title || selectedOrder.meta_description) && (
                      <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">SEO Meta</p>
                        {selectedOrder.meta_title && <p className="text-xs"><strong>Title:</strong> {selectedOrder.meta_title}</p>}
                        {selectedOrder.meta_description && <p className="text-xs"><strong>Description:</strong> {selectedOrder.meta_description}</p>}
                        {selectedOrder.image_alt && <p className="text-xs"><strong>Alt zdjecia:</strong> {selectedOrder.image_alt}</p>}
                      </div>
                    )}

                    {/* Tresc artykulu */}
                    {selectedOrder.content && (
                      <div className="space-y-1">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Tresc artykulu</p>
                        <div className="p-4 rounded-lg border bg-white dark:bg-slate-950 max-h-[300px] overflow-y-auto">
                          <SafeHtml className="prose prose-sm max-w-none dark:prose-invert" html={selectedOrder.content} />
                        </div>
                      </div>
                    )}

                    {/* Wymagania dodatkowe */}
                    {selectedOrder.requirements && (
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Dodatkowe uwagi klienta</p>
                        <p className="text-sm text-foreground">{selectedOrder.requirements}</p>
                      </div>
                    )}

                    <Separator />

                    {/* Akcje workflow */}
                    <Separator />
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-foreground">Realizacja zamowienia</p>

                      {/* Obrazek reklamodawcy */}
                      {(selectedOrder.image_data || selectedOrder.main_image_url || selectedOrder.advertiser_image) && (
                        <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                          <div className="flex items-center gap-2">
                            <Image className="w-4 h-4 text-slate-500" />
                            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Zdjecie wybrane przez reklamodawce</p>
                          </div>
                          <div className="relative">
                            <img 
                              src={selectedOrder.image_data || selectedOrder.main_image_url || selectedOrder.advertiser_image} 
                              alt="Zdjecie reklamodawcy"
                              className="w-full max-h-48 object-contain rounded-lg border bg-white"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                          <p className="text-[10px] text-slate-500">To zdjecie zostanie uzyte jako glowne zdjecie artykulu</p>
                        </div>
                      )}

                      {/* Krok 1: Generuj artykul AI (jesli wydawca pisze - brak content) */}
                      {!selectedOrder.content && !selectedOrder.generated_content && (
                        <Button 
                          className="w-full h-11 gap-2 bg-violet-600 hover:bg-violet-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed" 
                          onClick={async () => {
                            setGeneratingArticle(true);
                            toast.info("AI pisze artykul... To moze potrwac do 2 minut"); 
                            try {
                              const res = await axios.post(`${API}/admin/orders/${selectedOrder.id}/generate-article`);
                              toast.success(`Artykul wygenerowany: ${res.data.content_length} slow`);
                              fetchData(); 
                              // Odświeżamy dane zamówienia
                              const updated = await axios.get(`${API}/admin/orders`);
                              const updatedOrder = updated.data.find(o => o.id === selectedOrder.id);
                              if (updatedOrder) setSelectedOrder(updatedOrder);
                            } catch (err) { toast.error("Blad generowania"); }
                            finally { setGeneratingArticle(false); }
                          }}
                          disabled={generatingArticle}
                          data-testid="generate-article-btn"
                        >
                          {generatingArticle ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" /> Generowanie artykulu...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" /> Wygeneruj artykul AI (Grok)
                            </>
                          )}
                        </Button>
                      )}

                      {/* Nowy edytor WYSIWYG */}
                      {selectedOrder.generated_content && (
                        <ArticleEditor
                          initialTitle={selectedOrder.generated_title || ""}
                          initialContent={selectedOrder.generated_content || ""}
                          initialMetaTitle={selectedOrder.meta_title || ""}
                          initialMetaDesc={selectedOrder.meta_description || ""}
                          initialTags={selectedOrder.generated_tags || []}
                          initialImageAlt={selectedOrder.image_alt || ""}
                          targetPhrase={selectedOrder.target_phrase || ""}
                          targetUrl={selectedOrder.target_url || ""}
                          advertiserImage={selectedOrder.image_data || selectedOrder.main_image_url || selectedOrder.advertiser_image || ""}
                          saving={editorSaving}
                          continuing={editorContinuing}
                          regenerating={editorRegenerating}
                          onSave={async (title, content, metaTitle, metaDesc, tags, imageAlt) => {
                            setEditorSaving(true);
                            try {
                              await axios.post(`${API}/admin/orders/${selectedOrder.id}/update-article`, { 
                                title, content, meta_title: metaTitle, meta_description: metaDesc, tags, image_alt: imageAlt
                              });
                              toast.success("Zapisano wszystkie zmiany");
                              setSelectedOrder({
                                ...selectedOrder, 
                                generated_title: title, 
                                generated_content: content,
                                meta_title: metaTitle,
                                meta_description: metaDesc,
                                generated_tags: tags,
                                image_alt: imageAlt
                              });
                              fetchData();
                            } catch (err) { toast.error("Blad zapisu"); }
                            finally { setEditorSaving(false); }
                          }}
                          onContinue={async () => {
                            setEditorContinuing(true);
                            try {
                              const res = await axios.post(`${API}/admin/orders/${selectedOrder.id}/continue-article`);
                              toast.success(`AI dodalo ${res.data.added_words} slow`);
                              const updated = await axios.get(`${API}/admin/orders`);
                              const updatedOrder = updated.data.find(o => o.id === selectedOrder.id);
                              if (updatedOrder) setSelectedOrder(updatedOrder);
                              fetchData();
                            } catch (err) { toast.error("Blad kontynuowania"); }
                            finally { setEditorContinuing(false); }
                          }}
                          onRegenerate={async () => {
                            if (!confirm("Czy na pewno chcesz wygenerowac artykul od nowa? SEO meta rowniez zostanie wygenerowane ponownie.")) return;
                            setEditorRegenerating(true);
                            try {
                              const res = await axios.post(`${API}/admin/orders/${selectedOrder.id}/generate-article`);
                              toast.success(`Wygenerowano: ${res.data.content_length} slow, ${res.data.char_count} znakow`);
                              const updated = await axios.get(`${API}/admin/orders`);
                              const updatedOrder = updated.data.find(o => o.id === selectedOrder.id);
                              if (updatedOrder) setSelectedOrder(updatedOrder);
                              fetchData();
                            } catch (err) { toast.error("Blad generowania"); }
                            finally { setEditorRegenerating(false); }
                          }}
                        />
                      )}

                      {/* Info o zdjeciu */}
                      {selectedOrder.generated_image && (
                        <p className="text-[10px] text-slate-500 mt-2">
                          Zdjecie: {selectedOrder.generated_image.filename}
                        </p>
                      )}

                      {/* Krok 2: Publikuj */}
                      {(selectedOrder.content || selectedOrder.generated_content) && selectedOrder.status !== "completed" && (
                        <Button className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl" onClick={async () => {
                          toast.info("Publikowanie...");
                          try {
                            const res = await axios.post(`${API}/admin/orders/${selectedOrder.id}/publish`);
                            toast.success(res.data.url ? `Opublikowano: ${res.data.url}` : "Opublikowano w systemie");
                            fetchData(); setSelectedOrder(null);
                          } catch (err) { toast.error("Blad publikacji"); }
                        }}>
                          <Send className="w-4 h-4" /> Publikuj artykul na portalu
                        </Button>
                      )}

                      {/* Link do publikacji */}
                      {selectedOrder.published_url && (
                        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                          <p className="text-xs font-semibold text-emerald-700">Opublikowany</p>
                          <a href={selectedOrder.published_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                            {selectedOrder.published_url} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}

                      {/* Krok 3: Artykuly wspierajace */}
                      {selectedOrder.status === "completed" && !selectedOrder.support_articles && (
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
                          <p className="text-xs font-semibold text-blue-700">Artykuly wspierajace (SEO Boost)</p>
                          <p className="text-[10px] text-blue-600">AI wygeneruje artykuly z linkiem do glownego i opublikuje na innych portalach</p>
                          <div className="flex gap-2">
                            {[2, 3, 5].map(n => (
                              <Button key={n} variant="outline" size="sm" className="text-xs" onClick={async () => {
                                toast.info(`Generowanie ${n} artykulow wspierajacych...`);
                                try {
                                  const res = await axios.post(`${API}/admin/orders/${selectedOrder.id}/generate-support`, { count: n });
                                  toast.success(`Wygenerowano ${res.data.generated} artykulow wspierajacych`);
                                  fetchData(); setSelectedOrder(null);
                                } catch (err) { toast.error("Blad"); }
                              }}>Generuj {n} szt</Button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Raport artykulow wspierajacych */}
                      {selectedOrder.support_articles?.length > 0 && (
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
                          <p className="text-xs font-semibold text-blue-700">Artykuly wspierajace ({selectedOrder.support_articles.length} szt)</p>
                          {selectedOrder.support_articles.map((sa, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white border text-xs">
                              <div>
                                <p className="font-medium">{sa.title}</p>
                                <p className="text-[10px] text-slate-500">{sa.site} | {sa.words} slow</p>
                              </div>
                              <Badge variant="secondary" className="text-[9px]">W kolejce</Badge>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Status akcje */}
                      {selectedOrder.status === "pending" && (
                        <div className="flex gap-2">
                          <Button className="flex-1 gap-1 bg-emerald-600" onClick={() => handleOrderStatus(selectedOrder.id, "accepted")}>
                            <CheckCircle2 className="w-4 h-4" /> Zaakceptuj
                          </Button>
                          <Button variant="outline" className="flex-1 gap-1 text-red-600 border-red-200" onClick={() => handleOrderStatus(selectedOrder.id, "rejected")}>
                            <XCircle className="w-4 h-4" /> Odrzuc
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        {/* WITHDRAWALS */}
        <TabsContent value="withdrawals" className="mt-4">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4" /> Wnioski o wyplate</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">Brak wnioskow o wyplate</p>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((w) => (
                    <div key={w.id} className={`p-4 rounded-xl border-2 ${w.suspicious ? "border-red-300 bg-red-50 dark:bg-red-900/10" : "border-slate-100"}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold">{w.user_name || "Uzytkownik"}</p>
                          <p className="text-xs text-muted-foreground">{w.method.toUpperCase()} → {w.address}</p>
                          <p className="text-[10px] text-muted-foreground">{new Date(w.created_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">{w.amount} Zorkow</p>
                          <Badge className={`text-[9px] border-0 rounded-md ${
                            w.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                            w.status === "rejected" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"}`}>{w.status}</Badge>
                        </div>
                      </div>
                      {w.suspicious && (
                        <div className="p-2 rounded-lg bg-red-100 border border-red-300 text-xs text-red-800 mb-2">
                          <strong>PODEJRZANA TRANSAKCJA:</strong> {w.warning}
                        </div>
                      )}
                      {w.rejection_reason && (
                        <p className="text-xs text-red-600 mb-2">Powod odrzucenia: {w.rejection_reason}</p>
                      )}
                      {w.status === "pending" && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" className="gap-1 bg-emerald-600" onClick={async () => {
                            await axios.post(`${API}/admin/withdrawals/${w.id}/approve`);
                            toast.success("Zatwierdzone"); fetchData();
                          }}><CheckCircle2 className="w-3 h-3" /> Zatwierdz</Button>
                          <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Powod odrzucenia..." className="flex-1 h-8 text-xs" />
                          <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={async () => {
                            await axios.post(`${API}/admin/withdrawals/${w.id}/reject`, { reason: rejectReason || "Odrzucone" });
                            toast.success("Odrzucone - srodki zwrocone"); setRejectReason(""); fetchData();
                          }}><XCircle className="w-3 h-3" /> Odrzuc</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SITES - Widget embed */}
        <TabsContent value="sites" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Lista stron */}
            <Card className="lg:col-span-1 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4" /> Wszystkie strony ({allSites.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {allSites.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8">Brak stron</p>
                ) : (
                  <div className="space-y-1 max-h-[600px] overflow-y-auto">
                    {allSites.map((site) => (
                      <button
                        key={site.id}
                        onClick={() => setSelectedSiteForWidget(site)}
                        className={`w-full text-left p-3 rounded-lg transition-all ${
                          selectedSiteForWidget?.id === site.id 
                            ? "bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500" 
                            : "bg-muted/50 hover:bg-muted border-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{site.name}</p>
                            <p className="text-[10px] text-muted-foreground">{site.url}</p>
                          </div>
                          <Badge variant="secondary" className="text-[9px]">{site.category || "Brak"}</Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Widget embed - szczegoly */}
            <Card className="lg:col-span-2 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" /> 
                  {selectedSiteForWidget ? `Widget dla: ${selectedSiteForWidget.name}` : "Wybierz strone"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedSiteForWidget ? (
                  <div className="text-center py-12">
                    <Globe className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Wybierz strone z listy aby zobaczyc kod widgetu</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Info */}
                    <div className="p-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                      <h4 className="font-bold text-sm mb-1">Widget "Zamow reklame"</h4>
                      <p className="text-blue-100 text-xs">
                        Skopiuj kod i wklej na strone wydawcy. Widget pokaże oferte i przekieruje klientow do Epidemos.
                      </p>
                    </div>
                    
                    {/* Pelny widget */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Pelny widget (z animacja)
                      </label>
                      <div className="relative">
                        <pre className="p-3 rounded-lg bg-slate-900 text-green-400 text-[10px] overflow-x-auto whitespace-pre-wrap font-mono">
{`<!-- Epidemos Widget -->
<div id="epidemos-widget-${selectedSiteForWidget.id.substring(0, 8)}"></div>
<script>
(function() {
  var s = document.createElement("script");
  s.src = "https://epidemos.pl/api/embed/${selectedSiteForWidget.id}/script.js";
  s.async = true;
  document.getElementById("epidemos-widget-${selectedSiteForWidget.id.substring(0, 8)}").appendChild(s);
})();
</script>`}
                        </pre>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          className="absolute top-2 right-2 h-6 text-[10px] gap-1"
                          onClick={() => {
                            const code = `<!-- Epidemos Widget -->\n<div id="epidemos-widget-${selectedSiteForWidget.id.substring(0, 8)}"></div>\n<script>\n(function() {\n  var s = document.createElement("script");\n  s.src = "https://epidemos.pl/api/embed/${selectedSiteForWidget.id}/script.js";\n  s.async = true;\n  document.getElementById("epidemos-widget-${selectedSiteForWidget.id.substring(0, 8)}").appendChild(s);\n})();\n</script>`;
                            navigator.clipboard.writeText(code);
                            toast.success("Kod skopiowany!");
                          }}
                        >
                          Kopiuj
                        </Button>
                      </div>
                    </div>
                    
                    {/* Prosty link */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" /> Prosty przycisk (bez JS)
                      </label>
                      <div className="relative">
                        <pre className="p-3 rounded-lg bg-slate-900 text-green-400 text-[10px] overflow-x-auto whitespace-pre-wrap font-mono">
{`<a href="https://epidemos.pl/zamow/${selectedSiteForWidget.id}" 
   target="_blank"
   style="display:inline-block;padding:12px 24px;
          background:#2563eb;color:white;
          text-decoration:none;border-radius:8px;
          font-family:sans-serif;font-weight:bold;">
  Zamow reklame na tej stronie
</a>`}
                        </pre>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          className="absolute top-2 right-2 h-6 text-[10px] gap-1"
                          onClick={() => {
                            const code = `<a href="https://epidemos.pl/zamow/${selectedSiteForWidget.id}" target="_blank" style="display:inline-block;padding:12px 24px;background:#2563eb;color:white;text-decoration:none;border-radius:8px;font-family:sans-serif;font-weight:bold;">Zamow reklame na tej stronie</a>`;
                            navigator.clipboard.writeText(code);
                            toast.success("Kod skopiowany!");
                          }}
                        >
                          Kopiuj
                        </Button>
                      </div>
                    </div>
                    
                    {/* Bezposredni link */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" /> Bezposredni link
                      </label>
                      <div className="flex items-center gap-2">
                        <Input 
                          readOnly 
                          value={`https://epidemos.pl/zamow/${selectedSiteForWidget.id}`}
                          className="h-8 text-xs font-mono bg-muted flex-1"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8 text-xs shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(`https://epidemos.pl/zamow/${selectedSiteForWidget.id}`);
                            toast.success("Link skopiowany!");
                          }}
                        >
                          Kopiuj
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8 text-xs shrink-0"
                          onClick={() => window.open(`https://epidemos.pl/zamow/${selectedSiteForWidget.id}`, '_blank')}
                        >
                          Otworz
                        </Button>
                      </div>
                    </div>
                    
                    {/* Podglad */}
                    <div className="space-y-2 pt-2">
                      <label className="text-xs font-semibold text-foreground">Podglad widgetu</label>
                      <div className="p-4 rounded-lg bg-slate-100 dark:bg-slate-800 flex justify-center">
                        <div style={{
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                          borderRadius: "12px",
                          padding: "20px",
                          maxWidth: "280px",
                          boxShadow: "0 4px 20px rgba(37, 99, 235, 0.3)",
                          color: "white"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                            <div style={{ width: "28px", height: "28px", background: "white", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", color: "#2563eb", fontSize: "12px" }}>E</div>
                            <div style={{ fontSize: "12px", fontWeight: "600", opacity: "0.9" }}>Epidemos</div>
                          </div>
                          <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "8px" }}>Zamow reklame na tej stronie</div>
                          <div style={{ fontSize: "22px", fontWeight: "800", marginBottom: "14px" }}>
                            od 25 <span style={{ fontSize: "12px", fontWeight: "500", opacity: "0.8" }}>PLN</span>
                          </div>
                          <div style={{ display: "block", width: "100%", padding: "10px", background: "white", color: "#2563eb", borderRadius: "8px", fontSize: "13px", fontWeight: "700", textAlign: "center" }}>
                            Zamow teraz
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PRICING - Indywidualne ceny */}
        <TabsContent value="pricing" className="mt-4">
          <AdvertiserPricingManager />
        </TabsContent>

        {/* NEGOTIATIONS - Negocjacje */}
        <TabsContent value="negotiations" className="mt-4">
          <NegotiationsManager />
        </TabsContent>

        {/* BLOG - Posty na epidemos.pl */}
        <TabsContent value="blog" className="mt-4">
          {editingBlogPost ? (
            <BlogEditor
              post={editingBlogPost === "new" ? null : selectedBlogPost}
              onSave={() => {
                setEditingBlogPost(null);
                setSelectedBlogPost(null);
                fetchData();
              }}
              onCancel={() => {
                setEditingBlogPost(null);
                setSelectedBlogPost(null);
              }}
            />
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Blog epidemos.pl
                </CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => {
                    setEditingBlogPost("new");
                    setSelectedBlogPost(null);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Nowy post
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tytul</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Zaplanowano</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Akcje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blogPosts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                          Brak postow. Kliknij "Nowy post" aby dodac pierwszy.
                        </TableCell>
                      </TableRow>
                    ) : blogPosts.map((post) => (
                      <TableRow key={post.id}>
                        <TableCell className="font-medium max-w-[300px] truncate">{post.title}</TableCell>
                        <TableCell>
                          <Badge className={
                            post.status === "published" ? "bg-emerald-100 text-emerald-700" : 
                            post.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-600"
                          }>
                            {post.status === "published" ? "Opublikowany" : 
                             post.status === "scheduled" ? "Zaplanowany" : "Szkic"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {post.auto_schedule_days ? (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Clock className="w-3 h-3" />
                              Co {post.auto_schedule_days}d o {post.auto_schedule_hour || 8}:00
                            </span>
                          ) : post.scheduled_at ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(post.scheduled_at).toLocaleString('pl-PL')}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {post.published_at ? new Date(post.published_at).toLocaleDateString('pl-PL') : new Date(post.created_at).toLocaleDateString('pl-PL')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setSelectedBlogPost(post);
                              setEditingBlogPost(post.id);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (window.confirm("Usunac post?")) {
                                try {
                                  await axios.delete(`${API}/admin/blog/${post.id}`);
                                  toast.success("Post usuniety");
                                  fetchData();
                                } catch (err) {
                                  toast.error("Blad usuwania");
                                }
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Commission Settings */}
        <TabsContent value="commission" className="mt-4">
          <CommissionManager />
        </TabsContent>

        {/* SEO Settings */}
        <TabsContent value="seo" className="mt-4">
          <SeoSettingsManager />
        </TabsContent>

        <TabsContent value="legal" className="mt-4">
          <LegalPagesManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
